// src/lib/taxonomy/scale-rules.ts
// Scale-based inference rules for contract value and duration
// Infers capabilities from contract size and complexity

import { ScaleRule, DurationRule } from './types';

// ============================================================
// CONTRACT VALUE SCALE RULES
// ============================================================
export const VALUE_SCALE_RULES: ScaleRule[] = [
  {
    id: 'micro_purchase',
    minValue: 0,
    maxValue: 10000,
    label: 'Micro-Purchase',
    proves: [
      { capability: 'small_contract_execution', confidence: 0.70 },
      { capability: 'federal_contracting_experience', confidence: 0.60 }
    ],
    additionalContext: 'Simplified acquisition below micro-purchase threshold'
  },
  {
    id: 'small_purchase',
    minValue: 10001,
    maxValue: 250000,
    label: 'Small Purchase',
    proves: [
      { capability: 'small_contract_execution', confidence: 0.85 },
      { capability: 'federal_contracting_experience', confidence: 0.80 },
      { capability: 'contract_compliance', confidence: 0.75 }
    ],
    additionalContext: 'Simplified acquisition procedures typically apply'
  },
  {
    id: 'moderate_contract',
    minValue: 250001,
    maxValue: 1000000,
    label: 'Moderate Contract',
    proves: [
      { capability: 'contract_management', confidence: 0.85 },
      { capability: 'federal_contracting_experience', confidence: 0.85 },
      { capability: 'project_management', confidence: 0.80 },
      { capability: 'contract_compliance', confidence: 0.85 }
    ],
    additionalContext: 'Full and open competition typically required'
  },
  {
    id: 'significant_contract',
    minValue: 1000001,
    maxValue: 5000000,
    label: 'Significant Contract ($1M-$5M)',
    proves: [
      { capability: 'significant_contract_management', confidence: 0.90 },
      { capability: 'program_management', confidence: 0.85 },
      { capability: 'federal_contracting_experience', confidence: 0.90 },
      { capability: 'team_management', confidence: 0.85 },
      { capability: 'financial_management', confidence: 0.85 },
      { capability: 'contract_compliance', confidence: 0.90 }
    ],
    additionalContext: 'Demonstrates ability to manage contracts in the millions'
  },
  {
    id: 'large_contract',
    minValue: 5000001,
    maxValue: 10000000,
    label: 'Large Contract ($5M-$10M)',
    proves: [
      { capability: 'large_contract_management', confidence: 0.95 },
      { capability: 'program_management', confidence: 0.90 },
      { capability: 'enterprise_delivery', confidence: 0.85 },
      { capability: 'team_scaling', confidence: 0.90 },
      { capability: 'financial_management', confidence: 0.90 },
      { capability: 'risk_management', confidence: 0.85 },
      { capability: 'subcontractor_management', confidence: 0.80 }
    ],
    additionalContext: 'Demonstrates capability to deliver large-scale federal programs'
  },
  {
    id: 'major_contract',
    minValue: 10000001,
    maxValue: 50000000,
    label: 'Major Contract ($10M-$50M)',
    proves: [
      { capability: 'major_program_management', confidence: 0.95 },
      { capability: 'enterprise_delivery', confidence: 0.95 },
      { capability: 'large_team_management', confidence: 0.95 },
      { capability: 'complex_program_execution', confidence: 0.90 },
      { capability: 'financial_management', confidence: 0.95 },
      { capability: 'risk_management', confidence: 0.90 },
      { capability: 'subcontractor_management', confidence: 0.90 },
      { capability: 'earned_value_management', confidence: 0.85 }
    ],
    additionalContext: 'Demonstrates capability to manage major federal programs'
  },
  {
    id: 'enterprise_contract',
    minValue: 50000001,
    maxValue: 100000000,
    label: 'Enterprise Contract ($50M-$100M)',
    proves: [
      { capability: 'enterprise_program_management', confidence: 0.98 },
      { capability: 'enterprise_delivery', confidence: 0.98 },
      { capability: 'large_scale_operations', confidence: 0.95 },
      { capability: 'complex_program_execution', confidence: 0.95 },
      { capability: 'multi_site_operations', confidence: 0.90 },
      { capability: 'earned_value_management', confidence: 0.95 },
      { capability: 'prime_contractor_capability', confidence: 0.90 }
    ],
    additionalContext: 'Enterprise-scale federal program delivery'
  },
  {
    id: 'mega_contract',
    minValue: 100000001,
    label: 'Mega Contract ($100M+)',
    proves: [
      { capability: 'mega_program_management', confidence: 0.99 },
      { capability: 'enterprise_delivery', confidence: 0.99 },
      { capability: 'large_scale_operations', confidence: 0.98 },
      { capability: 'multi_site_operations', confidence: 0.95 },
      { capability: 'complex_program_execution', confidence: 0.98 },
      { capability: 'prime_contractor_capability', confidence: 0.98 },
      { capability: 'strategic_federal_partner', confidence: 0.90 }
    ],
    additionalContext: 'Proven capability to deliver the largest federal programs'
  }
];

// ============================================================
// CONTRACT DURATION SCALE RULES
// ============================================================
export const DURATION_SCALE_RULES: DurationRule[] = [
  {
    id: 'short_term',
    minMonths: 1,
    maxMonths: 6,
    label: 'Short-Term Contract',
    proves: [
      { capability: 'rapid_deployment', confidence: 0.85 },
      { capability: 'quick_start_capability', confidence: 0.90 },
      { capability: 'agile_delivery', confidence: 0.80 }
    ]
  },
  {
    id: 'medium_term',
    minMonths: 7,
    maxMonths: 12,
    label: 'Medium-Term Contract',
    proves: [
      { capability: 'sustained_delivery', confidence: 0.85 },
      { capability: 'project_lifecycle_management', confidence: 0.85 },
      { capability: 'team_retention', confidence: 0.80 }
    ]
  },
  {
    id: 'long_term',
    minMonths: 13,
    maxMonths: 36,
    label: 'Long-Term Contract (1-3 years)',
    proves: [
      { capability: 'long_term_program_delivery', confidence: 0.90 },
      { capability: 'sustained_operations', confidence: 0.90 },
      { capability: 'team_retention', confidence: 0.90 },
      { capability: 'continuous_improvement', confidence: 0.85 },
      { capability: 'relationship_management', confidence: 0.85 }
    ]
  },
  {
    id: 'extended_term',
    minMonths: 37,
    maxMonths: 60,
    label: 'Extended Contract (3-5 years)',
    proves: [
      { capability: 'extended_program_delivery', confidence: 0.95 },
      { capability: 'sustained_operations', confidence: 0.95 },
      { capability: 'workforce_stability', confidence: 0.90 },
      { capability: 'continuous_improvement', confidence: 0.90 },
      { capability: 'strategic_partnership', confidence: 0.85 },
      { capability: 'knowledge_management', confidence: 0.85 }
    ]
  },
  {
    id: 'idiq_bpa',
    minMonths: 61,
    label: 'Multi-Year IDIQ/BPA (5+ years)',
    proves: [
      { capability: 'idiq_management', confidence: 0.95 },
      { capability: 'long_term_strategic_delivery', confidence: 0.95 },
      { capability: 'task_order_management', confidence: 0.90 },
      { capability: 'workforce_stability', confidence: 0.95 },
      { capability: 'strategic_partnership', confidence: 0.95 },
      { capability: 'continuous_service_improvement', confidence: 0.90 }
    ]
  }
];

// ============================================================
// TEAM SIZE SCALE RULES
// ============================================================
export interface TeamSizeRule {
  id: string;
  minSize: number;
  maxSize?: number;
  label: string;
  proves: { capability: string; confidence: number }[];
}

export const TEAM_SIZE_RULES: TeamSizeRule[] = [
  {
    id: 'individual',
    minSize: 1,
    maxSize: 1,
    label: 'Individual Contributor',
    proves: [
      { capability: 'independent_work', confidence: 0.90 },
      { capability: 'self_direction', confidence: 0.85 }
    ]
  },
  {
    id: 'small_team',
    minSize: 2,
    maxSize: 5,
    label: 'Small Team',
    proves: [
      { capability: 'small_team_management', confidence: 0.85 },
      { capability: 'team_coordination', confidence: 0.85 }
    ]
  },
  {
    id: 'medium_team',
    minSize: 6,
    maxSize: 15,
    label: 'Medium Team',
    proves: [
      { capability: 'team_management', confidence: 0.90 },
      { capability: 'resource_coordination', confidence: 0.85 },
      { capability: 'project_staffing', confidence: 0.85 }
    ]
  },
  {
    id: 'large_team',
    minSize: 16,
    maxSize: 50,
    label: 'Large Team',
    proves: [
      { capability: 'large_team_management', confidence: 0.95 },
      { capability: 'multi_team_coordination', confidence: 0.90 },
      { capability: 'hr_management', confidence: 0.85 },
      { capability: 'workforce_planning', confidence: 0.85 }
    ]
  },
  {
    id: 'enterprise_team',
    minSize: 51,
    maxSize: 200,
    label: 'Enterprise Team',
    proves: [
      { capability: 'enterprise_team_management', confidence: 0.95 },
      { capability: 'organizational_management', confidence: 0.95 },
      { capability: 'hr_management', confidence: 0.95 },
      { capability: 'multi_site_staffing', confidence: 0.85 }
    ]
  },
  {
    id: 'major_program_team',
    minSize: 201,
    label: 'Major Program Team (200+)',
    proves: [
      { capability: 'major_program_staffing', confidence: 0.98 },
      { capability: 'organizational_management', confidence: 0.98 },
      { capability: 'large_scale_hr', confidence: 0.95 },
      { capability: 'multi_site_staffing', confidence: 0.95 },
      { capability: 'workforce_surge', confidence: 0.90 }
    ]
  }
];

/**
 * Find the value scale rule that applies to a given contract value
 */
export function findValueScaleRule(value: number): ScaleRule | undefined {
  return VALUE_SCALE_RULES.find(rule =>
    value >= rule.minValue &&
    (rule.maxValue === undefined || value <= rule.maxValue)
  );
}

/**
 * Find the duration scale rule that applies to a given duration in months
 */
export function findDurationScaleRule(months: number): DurationRule | undefined {
  return DURATION_SCALE_RULES.find(rule =>
    months >= rule.minMonths &&
    (rule.maxMonths === undefined || months <= rule.maxMonths)
  );
}

/**
 * Find the team size rule that applies to a given team size
 */
export function findTeamSizeRule(size: number): TeamSizeRule | undefined {
  return TEAM_SIZE_RULES.find(rule =>
    size >= rule.minSize &&
    (rule.maxSize === undefined || size <= rule.maxSize)
  );
}

/**
 * Calculate value similarity between two contract values
 * Returns 0-1 where 1 is within same scale tier
 */
export function calculateValueSimilarity(value1: number, value2: number): number {
  const tier1 = findValueScaleRule(value1);
  const tier2 = findValueScaleRule(value2);

  // Same tier
  if (tier1?.id === tier2?.id) return 1.0;

  // Calculate ratio-based similarity
  const ratio = Math.min(value1, value2) / Math.max(value1, value2);

  // Within 50% of each other
  if (ratio >= 0.5) return 0.9;

  // Within 25% of each other
  if (ratio >= 0.25) return 0.7;

  // Within 10% of each other
  if (ratio >= 0.1) return 0.5;

  // Very different scales
  return 0.2;
}

/**
 * Get all capabilities that can be proven by scale rules
 */
export function getAllScaleCapabilities(): string[] {
  const capabilities = new Set<string>();

  for (const rule of VALUE_SCALE_RULES) {
    for (const proof of rule.proves) {
      capabilities.add(proof.capability);
    }
  }

  for (const rule of DURATION_SCALE_RULES) {
    for (const proof of rule.proves) {
      capabilities.add(proof.capability);
    }
  }

  for (const rule of TEAM_SIZE_RULES) {
    for (const proof of rule.proves) {
      capabilities.add(proof.capability);
    }
  }

  return Array.from(capabilities).sort();
}

/**
 * Format contract value as human-readable string
 */
export function formatContractValue(value: number): string {
  if (value >= 1000000000) {
    return `$${(value / 1000000000).toFixed(1)}B`;
  } else if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  } else {
    return `$${value.toFixed(0)}`;
  }
}

/**
 * Format duration in months as human-readable string
 */
export function formatDuration(months: number): string {
  if (months >= 12) {
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    if (remainingMonths === 0) {
      return years === 1 ? '1 year' : `${years} years`;
    }
    return `${years} year${years > 1 ? 's' : ''}, ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
  }
  return months === 1 ? '1 month' : `${months} months`;
}
